import {
  InputChips,
  InputQuantity,
  InputTextArea,
} from '@farmshare/ui-components';
import { RecursivePartial } from '@farmshare/utils';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Stack,
  Typography,
} from '@mui/material';
import { FormikProps } from 'formik';
import { filter, map } from 'lodash';
import { useEffect } from 'react';

import { ProcessorCapability } from 'lib/graphql';

import {
  formatInputChipLabel,
  useCutsheetProcessorCapabilities,
} from './helpers';
import { CutsheetFormData, Extras } from './types';

interface NewCutsheetProps {
  fP: FormikProps<CutsheetFormData>;
  capabilities: RecursivePartial<ProcessorCapability>[];
  readOnly?: boolean;
}

export function CutsheetExtrasSection({
  fP,
  capabilities,
  readOnly = false,
}: NewCutsheetProps) {
  const processorCapabilities = useCutsheetProcessorCapabilities(
    fP,
    capabilities,
  );

  // Set the initial values for extras and trim based on the selected inspection level
  useEffect(() => {
    const selectedInspectionLevel = fP.values.inspectionLevel;

    if (selectedInspectionLevel) {
      const mappedExtras = map(
        filter(processorCapabilities?.extras, (extra) => extra?.isActive),
        (extra: Extras) => ({
          ...extra,
          isActive: false,
          quantity: extra?.minLbs ?? 0,
        }),
      ) as unknown as Extras[];

      if (fP.initialValues.inspectionLevel === selectedInspectionLevel) {
        if (fP.initialValues.extras?.length) {
          for (const extra of fP.initialValues.extras) {
            const index = mappedExtras.findIndex(
              (mappedExtra) =>
                mappedExtra.name.trim().toLowerCase() ===
                extra.name.trim().toLowerCase(),
            );
            if (index !== -1) {
              mappedExtras[index].isActive = true;
              mappedExtras[index].quantity = extra.quantity;
            }
          }
        }
      }

      fP.setFieldValue('extras', mappedExtras);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fP.values.inspectionLevel, processorCapabilities?.extras]);

  const { extras } = fP.values;

  if (!extras || extras.length === 0) {
    return null;
  }

  return (
    <Card sx={{ mb: 2, height: 1 }} elevation={1}>
      <CardHeader title="Extras" />
      <Stack direction="column" component={CardContent} spacing={2}>
        {map(extras, (item: Extras, index: number) => (
          <Stack
            spacing={1}
            alignItems="center"
            justifyContent="space-between"
            key={`${item.name}-${index}`}
            direction={{ xs: 'column', sm: 'row' }}
          >
            <InputChips
              disabled={readOnly}
              gap={2}
              options={[
                {
                  label: formatInputChipLabel(item),
                  value: item.name,
                  isActive: extras[index]?.isActive,
                },
              ]}
              size="sm"
              action={() => {
                const itemIndex = fP.values.extras?.findIndex(
                  (extra) => extra?.name === item.name,
                );
                if (itemIndex === undefined || itemIndex === -1) {
                  fP.setFieldValue(`extras.${index}`, {
                    ...item,
                    isActive: true,
                  });
                  return;
                }

                fP.setFieldValue(`extras.${itemIndex}`, {
                  ...item,
                  isActive: !fP.values.extras?.[itemIndex]?.isActive,
                });
                fP.setFieldTouched(`extras.${itemIndex}.quantity`, true);
              }}
            />

            {item.isActive && Boolean(item.minLbs) && (
              <InputQuantity
                disabled={readOnly}
                nameOverride={`extras.${index}.quantity`}
                step={1}
                min={item.minLbs}
              />
            )}
          </Stack>
        ))}
      </Stack>
      <CardContent>
        <Box sx={{ mt: 1.5 }}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 0.5, fontSize: '0.75rem' }}
          >
            Notes for Extras
          </Typography>
          <InputTextArea
            nameOverride="notes.extras"
            hint="Add any notes about your extras."
            disabled={readOnly}
            label="Notes"
            rows={2}
            showLabel={false}
          />
        </Box>
      </CardContent>
    </Card>
  );
}
